/**
 * ParticipantList Component - Displays room participants
 *
 * Shows a list of users currently in the collaborative editing session
 * with their assigned colors for visual distinction.
 *
 * @module features/collab/components/ParticipantList
 * @see SPEC-007 Requirements 4.6, 4.7, 4.10
 */

import type { RoomParticipant } from '@linkode/shared';
import { Users } from 'lucide-react';
import { useTypedTranslation } from '../../../i18n';

/**
 * Presence color palette (8 colors, matching colorId 0-7)
 * @see Requirement 4.10
 */
const PRESENCE_COLORS = [
  '#ef4444', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#14b8a6', // teal
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#ec4899', // pink
];

export interface ParticipantListProps {
  /** List of remote participants */
  participants: RoomParticipant[];
  /** Local user's display name */
  localDisplayName?: string;
  /** Local user's color ID */
  localColorId?: number;
  /** Whether to show the component in compact mode */
  compact?: boolean;
}

/**
 * ParticipantList - Displays room participants with color indicators
 *
 * @example
 * ```tsx
 * <ParticipantList
 *   participants={participants}
 *   localDisplayName="Alice"
 *   localColorId={0}
 * />
 * ```
 */
export function ParticipantList({
  participants,
  localDisplayName = 'You',
  localColorId = 0,
  compact = false,
}: ParticipantListProps) {
  const { t } = useTypedTranslation();
  const totalCount = participants.length + 1; // +1 for local user

  if (compact) {
    // Compact mode: just show count with icon
    return (
      <div
        className="flex items-center gap-1.5 px-2 py-1 text-xs"
        style={{
          fontFamily: 'var(--font-mono)',
          color: 'var(--color-text-secondary)',
          backgroundColor: 'var(--color-background)',
          border: 'var(--border-width) solid var(--border-color)',
          borderRadius: 'var(--radius-sm)',
        }}
        title={`${totalCount} participant${totalCount > 1 ? 's' : ''} in this document`}
      >
        <Users size={14} />
        <span>{totalCount}</span>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-2 px-2 py-1"
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: '12px',
        backgroundColor: 'var(--color-background)',
        border: 'var(--border-width) solid var(--border-color)',
        borderRadius: 'var(--radius-sm)',
      }}
    >
      {/* Participant avatars */}
      <div className="flex items-center -space-x-1.5">
        {/* Local user */}
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium ring-2 ring-white"
          style={{ backgroundColor: PRESENCE_COLORS[localColorId % PRESENCE_COLORS.length] }}
          title={`${localDisplayName} (You)`}
        >
          {localDisplayName.charAt(0).toUpperCase()}
        </div>

        {/* Remote participants (show up to 3) */}
        {participants.slice(0, 3).map((p) => (
          <div
            key={p.userId}
            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium ring-2 ring-white"
            style={{
              backgroundColor: PRESENCE_COLORS[p.presence.colorId % PRESENCE_COLORS.length],
            }}
            title={p.presence.displayName}
          >
            {p.presence.displayName.charAt(0).toUpperCase()}
          </div>
        ))}

        {/* Overflow indicator */}
        {participants.length > 3 && (
          // eslint-disable-next-line react/jsx-no-literals -- Symbol
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ring-2 ring-white"
            style={{
              backgroundColor: 'var(--color-text-secondary)',
              color: 'white',
            }}
            title={`${participants.length - 3} more participant${participants.length - 3 > 1 ? 's' : ''}`}
          >
            +{participants.length - 3}
          </div>
        )}
      </div>

      {/* Count label */}
      <span style={{ color: 'var(--color-text-secondary)' }}>
        {totalCount} {t('presence.online')}
      </span>
    </div>
  );
}
